import { useNavigate } from 'react-router';
import type { platform } from '@platform/contracts';
import {
  Alert,
  Badge,
  Box,
  BusinessIcon,
  Button,
  DataTable,
  EmptyState,
  MONO,
  Page,
  PageHeader,
  Stack,
  Typography,
  useToast,
  type Column,
} from '@platform/design-system';
import { describeError } from '@platform/app-kit';
import { usePackages, useSubscriptions, useTenants } from '../api/queries.ts';
import { formatDate } from '../shell/format.ts';
import { BillingStatusBadge, TenantStatusBadge, TierBadge } from '../shell/labels.tsx';

type Tenant = platform.Tenant;

/** `/tenants` — the registry with tier, status, cell, billing and the package snapshot. */
export function TenantsPage() {
  const tenants = useTenants();
  const packages = usePackages(true);
  const subscriptions = useSubscriptions();
  const navigate = useNavigate();
  const toast = useToast();
  const currentVersion = (packageId: string) =>
    packages.data?.find((p) => p.packageId === packageId)?.version;
  const packageName = (packageId: string) =>
    packages.data?.find((p) => p.packageId === packageId)?.name ?? packageId;
  const billingOf = (tenantId: string) => subscriptions.data?.find((s) => s.tenantId === tenantId);

  const columns: Column<Tenant>[] = [
    {
      id: 'name',
      header: 'Tenant',
      sortValue: (t) => t.name,
      cell: (t) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {t.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.tertiary"
            sx={{ ...MONO, display: 'block' }}
            noWrap
          >
            {t.slug}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'tier',
      header: 'Tier',
      sortValue: (t) => t.tier,
      cell: (t) => <TierBadge tier={t.tier} />,
    },
    {
      id: 'status',
      header: 'Status',
      sortValue: (t) => t.status,
      cell: (t) => <TenantStatusBadge status={t.status} />,
    },
    {
      id: 'billing',
      header: 'Billing',
      sortValue: (t) => billingOf(t.tenantId)?.status ?? '',
      cell: (t) => {
        const s = billingOf(t.tenantId);
        return s ? <BillingStatusBadge status={s.status} /> : <Badge label="Unknown" />;
      },
    },
    {
      id: 'cell',
      header: 'Cell',
      sortValue: (t) => t.cellId,
      cell: (t) => (
        <Box component="span" sx={MONO}>
          {t.cellId}
        </Box>
      ),
    },
    {
      id: 'package',
      header: 'Package',
      sortValue: (t) =>
        t.entitlement
          ? `${packageName(t.entitlement.packageId)} ${t.entitlement.packageVersion}`
          : '',
      cell: (t) => {
        if (!t.entitlement) return '—';
        const now = currentVersion(t.entitlement.packageId);
        const behind = now !== undefined && now > t.entitlement.packageVersion;
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <span>
              {packageName(t.entitlement.packageId)} · v{t.entitlement.packageVersion}
            </span>
            {behind && <Badge tone="info" label={`v${now} available`} />}
          </Stack>
        );
      },
    },
    {
      id: 'seats',
      header: 'Seats',
      numeric: true,
      sortValue: (t) => billingOf(t.tenantId)?.seats.licensed ?? 0,
      cell: (t) => {
        const s = billingOf(t.tenantId);
        return s ? `${s.seats.used} / ${s.seats.licensed}` : '—';
      },
    },
    {
      id: 'created',
      header: 'Created',
      sortValue: (t) => new Date(t.createdAt),
      cell: (t) => formatDate(t.createdAt),
    },
  ];

  return (
    <Page>
      <PageHeader
        title="Tenants"
        meta={
          tenants.data
            ? `${tenants.data.length} tenants across ${new Set(tenants.data.map((t) => t.cellId)).size} ${new Set(tenants.data.map((t) => t.cellId)).size === 1 ? 'cell' : 'cells'}`
            : undefined
        }
        actions={
          <Button
            variant="contained"
            onClick={() =>
              toast.info(
                'Sales-led onboarding (Tenants_create) arrives with the onboarding workflow',
              )
            }
          >
            Onboard tenant
          </Button>
        }
      />
      {tenants.isError && (
        <Alert severity="error">{describeError(tenants.error, 'Could not load the tenants')}</Alert>
      )}
      <DataTable
        label="Tenants"
        columns={columns}
        rows={tenants.data ?? []}
        getRowId={(t) => t.tenantId}
        loading={tenants.isLoading}
        defaultSort={{ columnId: 'name', direction: 'asc' }}
        onRowClick={(t) => void navigate(`/tenants/${t.tenantId}`)}
        emptyState={
          <EmptyState
            plain
            icon={<BusinessIcon />}
            title="No tenants yet"
            description="Tenants appear here as the onboarding workflow provisions them."
            action={<Button variant="contained">Onboard tenant</Button>}
          />
        }
      />
    </Page>
  );
}
